import React from "react";
import { InfoCard, InfoCardProps } from "./InfoCard";

import Logo from "@/assets/logo_name.svg";
import CompanyLogo1 from "@/assets/companies/healthgrades.svg";
import CompanyLogo2 from "@/assets/companies/bluecross_blueshield.svg";
import CompanyLogo3 from "@/assets/companies/allscripts.svg";
import CompanyLogo4 from "@/assets/companies/sutter_health.svg";
import DotPattern from "../ui/dot-pattern";

interface BaseProps {
  title: string | React.JSX.Element;
  secondarySubtitle?: string;
  subtitle?: string;
  step?: number;
  stepTotal?: number;
  form: React.ReactNode;
}
type Props = BaseProps &
  (
    | {
        infoCard: InfoCardProps & { mobileFragments?: boolean };
        infoCardContent?: React.ReactNode;
      }
    | {
        infoCard?: InfoCardProps & { mobileFragments?: boolean };
        infoCardContent: React.ReactNode;
      }
  );

export const PrescreenLayout = ({
  title,
  secondarySubtitle,
  subtitle,
  step,
  stepTotal,
  form,
  infoCard,
  infoCardContent,
}: Props) => (
  <div className="absolute top-0 left-0 bg-background w-screen h-screen max-h-full flex flex-col md:flex-row items-stretch md:justify-center z-10 overflow-y-auto">
    <div className="hidden md:block max-h-full">
      {infoCard ? (
        <InfoCard {...infoCard} />
      ) : (
        <InfoCard contentOverride={infoCardContent} />
      )}
    </div>
    <div className="md:p-8 w-full flex flex-col gap-7">
      <div className="flex flex-col gap-4">
        <div className="px-4 pt-4 pb-2 md:px-0 md:py-4">
          <img
            className="h-9 object-contain self-start"
            src={Logo}
            alt="Venturous logo"
          />
        </div>
        {infoCard?.mobileFragments && infoCard.title && (
          <div className="md:hidden px-4 py-8 relative w-full h-full max-h-full min-h-0 flex flex-col gap-10 items-center justify-center text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-accent to-secondary-dark to-40% -z-[3]" />
            <DotPattern
              width={24}
              height={24}
              className="absolute inset-0 min-w-full opacity-50 -z-[1]"
            />
            <h1 className="max-w-[75%] text-secondary-foreground text-3xl">
              {infoCard.title}
            </h1>
          </div>
        )}
        <div className="px-4 md:px-0 w-full flex flex-col items-start gap-1">
          {Boolean(step) && (
            <p className="text-sm font-medium text-inline-link">
              Step {step}
              {stepTotal ? ` of ${stepTotal}` : undefined}
            </p>
          )}
          <h2 className="text-3xl text-[#2E2E38] font-bold">{title}</h2>
          {(secondarySubtitle ?? subtitle) ? (
            <div className="flex flex-row items-center gap-2">
              {secondarySubtitle && (
                <h3 className="text-lg text-[#2E2E38] font-medium">
                  {secondarySubtitle}
                </h3>
              )}
              {subtitle && (
                <h3 className="text-base text-[#2E2E38] font-medium">
                  {subtitle}
                </h3>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="px-4 md:px-0">{form}</div>
      {infoCard?.mobileFragments && infoCard.footer && (
        <div className="md:hidden md-auto flex flex-col gap-4">
          <div className="px-4 py-8 relative w-full h-full max-h-full min-h-0 flex flex-col gap-10 items-center justify-center text-center overflow-hidden">
            <div className="absolute inset-0 bg-secondary-dark -z-[3]" />
            <DotPattern
              width={24}
              height={24}
              className="absolute inset-0 min-w-full opacity-50 -z-[1]"
            />
            <p className="text-secondary-foreground">
              Featuring top talent from industry leaders
            </p>
            <div className="relative w-full flex flex-row items-center justify-between max-h-8 -z-[2] gap-4">
              <div className="absolute w-full h-full bg-gradient-to-r from-secondary-dark via-50% via-secondary-foreground/0 to-secondary-dark overflow-hidden" />
              <img src={CompanyLogo1} alt="Healthgrades" />
              <img src={CompanyLogo2} alt="BlueCross BlueShield" />
              <img src={CompanyLogo3} alt="Allscripts" />
              <img src={CompanyLogo4} alt="Sutter Health" />
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
